import type { Address, Hex } from 'viem'

import { MathLib } from '@morpho-org/blue-sdk'
import { formatUnits } from 'viem'

import type {
  PublicAllocatorSettings,
  PublicAllocatorSettingsMarket
} from '@/lib/procedures/publicAllocatorSettings.contract'

export function formatPenalty(penalty: bigint) {
  // A WAD rate becomes a percentage with 16 decimal places.
  const exact = `${formatUnits(penalty, 16)}%`
  if (penalty === 0n) return { display: 'No penalty', exact, isBelowThreshold: false }
  if (penalty < 10n ** 12n) return { display: '<0.0001%', exact, isBelowThreshold: true }
  const rounded = (penalty + 5n * 10n ** 11n) / 10n ** 12n
  return { display: `${formatUnits(rounded, 4)}%`, exact, isBelowThreshold: false }
}

/**
 * The Vault's allocation as a WAD share of a Public Allocator cap, or `null` when the cap is zero.
 * Not clamped: the share is above 1 when a cap is set below the current allocation.
 */
export function getCapUsage(allocation: bigint, absoluteCap: bigint) {
  if (absoluteCap === 0n) return null
  return MathLib.wDivDown(allocation, absoluteCap)
}

export interface PublicAllocatorMarketKey {
  marketId: Hex
  adapter: Address
}

export type PublicAllocatorRowState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'inactive' }
  | { status: 'active'; candidate: PublicAllocatorSettingsMarket }

export function buildPublicAllocatorRows(
  markets: readonly PublicAllocatorMarketKey[],
  data: PublicAllocatorSettings
): Map<string, PublicAllocatorRowState> {
  const rows = new Map<string, PublicAllocatorRowState>()
  for (const market of markets) {
    const candidate = data.markets.find(
      item =>
        item.isActiveAdapter &&
        item.marketId.toLowerCase() === market.marketId.toLowerCase() &&
        item.adapter.toLowerCase() === market.adapter.toLowerCase()
    )
    rows.set(
      `${market.marketId}:${market.adapter.toLowerCase()}`,
      candidate ? { status: 'active', candidate } : { status: 'inactive' }
    )
  }
  return rows
}
